import './ChartRenderer.scss';
import cn from 'classnames';
import { useMemo } from 'react';
import {
  Area,
  AxisDomainItem,
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import {
  CellValue,
  ChartConfig,
  QueryResponse,
  SeriesMark
} from '@blisspointmedia/bpm-types/dist/dashboarding';
import { useDynamicYAxisWidth } from '@Components/Charts/ChartUtils';
import {
  AXIS_TICK_FONT_SIZE,
  AXIS_TITLE_FONT_SIZE,
  AXIS_TITLE_GUTTER,
  CHART_MARGIN,
  LEGEND_FONT_SIZE,
  NUMERIC_X_CHART_MARGIN,
  MAX_BAR_SIZE,
  OVERLAY_PALETTE,
  SERIES_PALETTE
} from './chart-renderer-constants';
import {
  ComparisonPeriod,
  formatAxisTick,
  formatChartValue,
  formatComparisonDate,
  formatXTick,
  orderTooltipEntries,
  PreparedGraphicChart,
  PreparedSeries,
  prepareGraphicChart,
  resolveNumericXAxis,
  resolveValueAxes,
  truncateAxisTitle,
  ValueAxis
} from './chart-renderer-utils';
import { ChartPlaceholder } from './ChartPlaceholder';
import { PieRenderer } from './PieRenderer';
import { StatRenderer } from './StatRenderer';
import { TableRenderer } from './TableRenderer';
import { useFieldMetaCtx } from './useFieldMetaCtx';

interface ChartRendererProps {
  config: ChartConfig;
  data: QueryResponse;
  className?: string;
  // When false, the chart body draws no title (the dashboard card header owns it). Defaults to true
  // so standalone uses (the Dashboard Agent canvas, the focused single-chart view) keep their title.
  showTitle?: boolean;
  // Live breakdown dimension from the picker (a bare dimension name or a qualified ref); picks the
  // color split once the payload grouped by it lands. Kept out of config per the controls invariant.
  breakdownRef?: string;
}

// Default color per series: split series cycle the categorical palette, overlay lines take the
// overlay palette by their own position so they never share a channel's hue.
const seriesColors = (series: PreparedSeries[]): string[] => {
  let overlayIndex = 0;
  return series.map((s, i) => {
    if (s.color) return s.color;
    if (!s.overlay) return SERIES_PALETTE[i % SERIES_PALETTE.length];
    const color = OVERLAY_PALETTE[overlayIndex % OVERLAY_PALETTE.length];
    overlayIndex += 1;
    return color;
  });
};

// Width of the category (y) axis on a horizontal bar chart, before any title.
const HORIZONTAL_CATEGORY_AXIS_WIDTH = 96;
type AxisDomain = [AxisDomainItem, AxisDomainItem];

// Height of a bottom axis that carries a title: recharts' default 30px for the ticks, plus the title.
const TITLED_X_AXIS_HEIGHT = 48;
// A scatter's numeric x spans just its data when there are no ticks to fix it (recharts' default
// [0, 'auto'] would pin it to zero).
const SCATTER_X_DOMAIN: AxisDomain = ['auto', 'auto'];

// Props every numeric value axis shares. Its precomputed ticks also fix its domain, so recharts
// draws exactly the ticks the shared tick scale was chosen for; with no ticks (no numeric data)
// recharts places its own over `fallbackDomain`.
const valueAxisProps = (axis: ValueAxis, fallbackDomain: AxisDomain = [0, 'auto']) => ({
  type: 'number' as const,
  ticks: axis.ticks,
  domain: axis.ticks ? [axis.ticks[0], axis.ticks[axis.ticks.length - 1]] : fallbackDomain,
  tickFormatter: (value: CellValue) => formatAxisTick(value, axis.format, axis.tickScale),
  tickLine: false,
  tick: { fontSize: AXIS_TICK_FONT_SIZE }
});

type AxisTitleSide = 'left' | 'right' | 'bottom';

// An axis' `label` prop: the title element, or nothing when the axis has no title.
const axisTitle = (title: string, side: AxisTitleSide) =>
  title ? <AxisTitle axisTitle={title} side={side} /> : undefined;

// Renders a ChartConfig + its QueryResponse. Graphic marks (line/area/bar/point) draw through a
// single ComposedChart so per-series marks (combo charts) compose for free. `table` delegates to the
// bespoke TableRenderer; `stat` delegates to the StatRenderer scorecard; `pie` delegates to PieRenderer.
export const ChartRenderer = ({
  config,
  data,
  className,
  showTitle = true,
  breakdownRef
}: ChartRendererProps) => {
  // Formatting context (currency + this chart's custom metrics), memoized. Threaded into the prep
  // layer so every series/cell/value formats via the metric-column-system registry, currency-correct,
  // and so a custom metric renders with its display name rather than its raw id.
  const ctx = useFieldMetaCtx(config);
  // Memoized so the Y-axis gutter hook below can key off `prepared.rows`, which changes exactly when
  // the displayed data changes — unlike the raw `data` prop, which stays the same object when only the
  // Metric/Breakdown picker re-pivots it. Returns null for table/stat/pie, so those pay nothing.
  const prepared = useMemo(
    () => prepareGraphicChart(config, data, ctx, breakdownRef),
    [config, data, ctx, breakdownRef]
  );
  const { yAxisWidth, setChartRef } = useDynamicYAxisWidth({ reCalcDependency: prepared?.rows });

  if (config.type === 'table') {
    return (
      <TableRenderer
        config={config}
        data={data}
        className={className}
        showTitle={showTitle}
        ctx={ctx}
      />
    );
  }
  if (config.type === 'stat') {
    return (
      <StatRenderer
        config={config}
        data={data}
        className={className}
        showTitle={showTitle}
        ctx={ctx}
      />
    );
  }

  if (config.type === 'pie') {
    return (
      <PieRenderer
        config={config}
        data={data}
        className={className}
        showTitle={showTitle}
        ctx={ctx}
      />
    );
  }

  if (!prepared) {
    return (
      <ChartPlaceholder
        className={className}
        message="Couldn't map this chart's data to its encoding."
      />
    );
  }
  // No series to draw (e.g. a color split over an empty result).
  if (prepared.series.length === 0) {
    return <ChartPlaceholder className={className} message='No data for this chart.' />;
  }

  const { encoding } = config;
  // Captured here so the narrowing (pie/table/stat returned above) survives into the series callback.
  const baseMark = config.type;
  const stacked = encoding.stack === true || encoding.grouping === 'stacked';
  const horizontal = encoding.orientation === 'horizontal';
  // Dual-axis: series bound to both value axes (e.g. spend bars on the left, impressions line on the
  // right). Only in the standard vertical layout — a right value axis is meaningless for horizontal bars.
  const { dualAxis, left, right } = resolveValueAxes(prepared, { horizontal, stacked, baseMark });
  const colors = seriesColors(prepared.series);
  // The category axis is named after its dimension, except a date axis, which reads as dates on sight.
  const categoryTitle = prepared.xIsDate ? '' : (prepared.xLabel ?? '');

  const categoryAxis = (
    <XAxis
      dataKey={prepared.xKey}
      type='category'
      tickFormatter={value => formatXTick(value, prepared)}
      tickLine={false}
      tick={{ fontSize: AXIS_TICK_FONT_SIZE }}
      height={categoryTitle ? TITLED_X_AXIS_HEIGHT : undefined}
      label={axisTitle(categoryTitle, 'bottom')}
    />
  );
  // A scatter's x is a continuous measure (e.g. spend), so it gets a numeric axis spanning the data
  // range — not a category axis of every discrete x value.
  const numericX = prepared.xIsNumeric ? resolveNumericXAxis(prepared) : null;
  const numericXAxis = numericX && (
    <XAxis
      dataKey={prepared.xKey}
      {...valueAxisProps(numericX, SCATTER_X_DOMAIN)}
      height={numericX.label ? TITLED_X_AXIS_HEIGHT : undefined}
      label={axisTitle(numericX.label, 'bottom')}
    />
  );
  // The bottom (x) axis in the standard vertical layout: numeric for a scatter, category otherwise.
  const bottomXAxis = numericXAxis ?? categoryAxis;
  const valueAxis = (
    <YAxis
      // In dual-axis mode this is the left axis; recharts needs the id so left-bound series find it.
      yAxisId={dualAxis ? 'left' : undefined}
      // Scatter only: bind the y-axis to the y metric so its scale spans the data range. Without a
      // dataKey recharts can't derive the domain from the points and collapses them onto the baseline.
      // Line/bar/area leave it unset — each series supplies its own y via its own dataKey.
      dataKey={prepared.xIsNumeric ? prepared.series[0].key : undefined}
      {...valueAxisProps(left)}
      width={yAxisWidth + (left.label ? AXIS_TITLE_GUTTER : 0)}
      label={axisTitle(left.label, 'left')}
    />
  );
  // Second value axis on the right, drawn only when a series binds to it (dual-axis charts).
  const rightValueAxis = right ? (
    <YAxis
      yAxisId='right'
      orientation='right'
      {...valueAxisProps(right)}
      width={yAxisWidth + (right.label ? AXIS_TITLE_GUTTER : 0)}
      label={axisTitle(right.label, 'right')}
    />
  ) : null;

  return (
    <div className={cn('chart-renderer', className)}>
      {showTitle && <h3 className='chart-renderer-title'>{config.title}</h3>}
      {prepared.notice && <div className='chart-renderer-notice'>{prepared.notice}</div>}
      <div className='chart-renderer-canvas'>
        <ResponsiveContainer width='100%' height='100%'>
          <ComposedChart
            ref={setChartRef}
            data={prepared.rows}
            margin={horizontal || prepared.xIsNumeric ? NUMERIC_X_CHART_MARGIN : CHART_MARGIN}
            layout={horizontal ? 'vertical' : 'horizontal'}
          >
            <CartesianGrid strokeDasharray='3 3' vertical={horizontal} horizontal={!horizontal} />
            {horizontal ? (
              <XAxis
                {...valueAxisProps(left)}
                height={left.label ? TITLED_X_AXIS_HEIGHT : undefined}
                label={axisTitle(left.label, 'bottom')}
              />
            ) : (
              bottomXAxis
            )}
            {horizontal ? (
              <YAxis
                dataKey={prepared.xKey}
                type='category'
                tickFormatter={value => formatXTick(value, prepared)}
                tickLine={false}
                width={HORIZONTAL_CATEGORY_AXIS_WIDTH + (categoryTitle ? AXIS_TITLE_GUTTER : 0)}
                tick={{ fontSize: AXIS_TICK_FONT_SIZE }}
                label={axisTitle(categoryTitle, 'left')}
              />
            ) : (
              valueAxis
            )}
            {rightValueAxis}
            <Tooltip content={<ChartTooltip prepared={prepared} />} />
            {prepared.series.length > 1 && (
              // Draw order, not recharts' default A–Z: a split's largest series first.
              <Legend wrapperStyle={{ fontSize: LEGEND_FONT_SIZE }} itemSorter={null} />
            )}
            {prepared.series.map((s, i) =>
              renderSeries({
                series: s,
                index: i,
                baseMark,
                color: colors[i],
                stackId: stacked && !s.overlay ? 'stack' : undefined,
                dualAxis
              })
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

interface AxisTitleProps {
  axisTitle: string;
  side: AxisTitleSide;
  // Injected by recharts when it renders the element as the axis label: the axis' own box.
  viewBox?: { x?: number; y?: number; width?: number; height?: number };
}

// An axis title, drawn inside the axis' box: rotated along a vertical axis, centred under a
// horizontal one. A title too long for the axis is cut with an ellipsis; the full title shows on
// hover via the SVG <title>.
const AxisTitle = ({ axisTitle: title, side, viewBox }: AxisTitleProps) => {
  if (!viewBox) return null;
  const { x = 0, y = 0, width = 0, height = 0 } = viewBox;
  const half = AXIS_TITLE_FONT_SIZE / 2;
  const placement = {
    left: { cx: x + half, cy: y + height / 2, angle: -90, length: height },
    right: { cx: x + width - half, cy: y + height / 2, angle: 90, length: height },
    bottom: { cx: x + width / 2, cy: y + height - half, angle: 0, length: width }
  }[side];
  const { cx, cy, angle, length } = placement;
  return (
    <text
      className='chart-renderer-axis-title'
      x={cx}
      y={cy}
      transform={angle ? `rotate(${angle} ${cx} ${cy})` : undefined}
      textAnchor='middle'
      dominantBaseline='central'
      fontSize={AXIS_TITLE_FONT_SIZE}
    >
      <title>{title}</title>
      {truncateAxisTitle(title, length, AXIS_TITLE_FONT_SIZE)}
    </text>
  );
};

interface RenderSeriesArgs {
  series: PreparedSeries;
  // Position in the series list — folded into the React key so series that share a dataKey (a
  // degenerate config emitting the same ref twice) don't collide and trigger duplicate-key warnings.
  index: number;
  baseMark: SeriesMark;
  color: string;
  stackId?: string;
  // When the chart has a right value axis, bind each series to 'left'/'right' by its `side`. Left
  // undefined otherwise so single-axis charts keep recharts' default axis.
  dualAxis: boolean;
}

// recharts strokeDasharray for a dashed series (e.g. a comparison-period overlay).
const DASHED_STROKE = '4 4';

// Build one value series element with its effective mark (per-series override, else the chart's base
// mark). This MUST be a plain function, not a component — ComposedChart introspects its direct
// children's component types, so a custom wrapper component's series would never be drawn.
const renderSeries = ({ series, index, baseMark, color, stackId, dualAxis }: RenderSeriesArgs) => {
  const mark = series.mark ?? baseMark;
  const key = `${series.key}::${index}`;
  const dualAxisSide = series.side === 'right' ? 'right' : 'left';
  const yAxisId = dualAxis ? dualAxisSide : undefined;
  const dash = series.dashed ? DASHED_STROKE : undefined;
  switch (mark) {
    case 'area':
      return (
        <Area
          key={key}
          yAxisId={yAxisId}
          dataKey={series.key}
          name={series.label}
          type='monotone'
          stroke={color}
          strokeDasharray={dash}
          fill={color}
          fillOpacity={0.2}
          stackId={stackId}
          connectNulls={false}
        />
      );
    case 'bar':
      return (
        <Bar
          key={key}
          yAxisId={yAxisId}
          dataKey={series.key}
          name={series.label}
          fill={color}
          // Bars ignore strokeDasharray, so a color-matched prior bar (breakdown comparison) reads as
          // "prior" via a lighter fill instead of a dash. Non-comparison bars keep full opacity.
          fillOpacity={series.period === 'prior' ? 0.55 : undefined}
          maxBarSize={MAX_BAR_SIZE}
          stackId={stackId}
        />
      );
    case 'point':
      return (
        <Scatter
          key={key}
          yAxisId={yAxisId}
          // A period-over-period scatter draws two clouds; each carries its OWN points so both bind to
          // the shared numeric x/y axis. `undefined` for a single cloud → recharts uses the chart data.
          data={series.points}
          dataKey={series.key}
          // The period name ("This period"/"Prior period") drives the legend + tooltip for a comparison
          // scatter; a single cloud falls back to the metric label.
          name={series.pointGroupLabel ?? series.label}
          fill={color}
        />
      );
    default:
      return (
        <Line
          key={key}
          yAxisId={yAxisId}
          dataKey={series.key}
          name={series.label}
          type='monotone'
          stroke={color}
          strokeDasharray={dash}
          strokeWidth={2}
          strokeLinecap='round'
          strokeLinejoin='round'
          dot={false}
          // recharts' default active dot draws a white ring (stroke: '#fff') that punches a halo
          // into whichever line passes underneath at a crossing — drop the ring so crossings read clean.
          activeDot={{ strokeWidth: 0 }}
          connectNulls={false}
        />
      );
  }
};

// One entry recharts injects into the tooltip per rendered series at the hovered position.
interface TooltipEntry {
  dataKey?: string | number;
  value?: number | string | null;
  // The rendered series' name (recharts passes the <Scatter name>). For a comparison scatter this is
  // the hovered cloud's period ("This period"/"Prior period") — the tooltip shows it as the header.
  name?: string;
  // The full row backing this entry — used by the scatter tooltip to read the point's x and label.
  payload?: Record<string, CellValue>;
}

interface ChartTooltipProps {
  prepared: PreparedGraphicChart;
  // recharts injects these when it renders the tooltip content
  active?: boolean;
  label?: string | number;
  payload?: TooltipEntry[];
}

const seriesByKey = (series: PreparedSeries[]): Record<string, PreparedSeries> =>
  Object.fromEntries(series.map(s => [s.key, s]));

interface ScatterTooltipProps {
  prepared: PreparedGraphicChart;
  payload?: TooltipEntry[];
}

// A scatter point's tooltip: its label (e.g. campaign) as the header, then the x and y coordinates —
// rather than the per-series listing used by line/bar/area charts.
const ScatterTooltip = ({ prepared, payload }: ScatterTooltipProps) => {
  const entry = payload?.[0];
  const row = entry?.payload;
  const series = prepared.series[0];
  const pointLabel = prepared.pointLabelKey && row ? row[prepared.pointLabelKey] : undefined;
  // Read BOTH coordinates straight from the point's row — never recharts' `entry.value`, which for a
  // scatter can surface the x value and made revenue read identical to spend.
  const xValue = row ? row[prepared.xKey] : null;
  const yValue = row && series ? row[series.key] : (entry?.value ?? null);

  // A comparison scatter (2+ clouds) tags the hovered point with its period via the series name.
  const period = prepared.series.length > 1 ? entry?.name : undefined;
  return (
    <div className='chart-renderer-tooltip'>
      {pointLabel !== undefined && pointLabel !== null && pointLabel !== '' && (
        <div className='chart-renderer-tooltip-label'>{String(pointLabel)}</div>
      )}
      {period && <div className='chart-renderer-tooltip-label'>{period}</div>}
      <div className='chart-renderer-tooltip-row'>
        <span className='chart-renderer-tooltip-name'>{prepared.xLabel}</span>
        <span className='chart-renderer-tooltip-value'>
          {prepared.xFormatValue
            ? prepared.xFormatValue(xValue)
            : formatChartValue(xValue, prepared.xFormat)}
        </span>
      </div>
      <div className='chart-renderer-tooltip-row'>
        <span className='chart-renderer-tooltip-name'>{series?.label}</span>
        <span className='chart-renderer-tooltip-value'>
          {series ? series.formatValue(yValue) : formatChartValue(yValue)}
        </span>
      </div>
    </div>
  );
};

const PERIOD_TITLES: Record<ComparisonPeriod, string> = { current: 'Current', prior: 'Prior' };

const ChartTooltip = ({ prepared, active, label, payload }: ChartTooltipProps) => {
  if (!active || !payload?.length) return null;
  if (prepared.xIsNumeric) return <ScatterTooltip prepared={prepared} payload={payload} />;
  const lookup = seriesByKey(prepared.series);
  // Period-over-period overlay: series carry a `period` ('current'/'prior') and period-suffixed labels
  // ("Meta · prior"), so the row label says which window.
  const isComparison = prepared.series.some(s => Boolean(s.period));
  // A single-metric comparison names that metric as the header (rows carry the period); otherwise — and
  // for every non-comparison chart — the hovered x value (date/category) anchors the rows.
  const header =
    isComparison && prepared.comparisonMetricLabel
      ? prepared.comparisonMetricLabel
      : formatXTick(label ?? null, prepared);
  // With the metric in the header, each row names its window by period AND date ("Prior · 9/23/24"),
  // since the two windows' dates differ and the axis only shows the current one.
  const row = payload[0]?.payload;
  const datedPeriodLabel = (s: PreparedSeries): string | undefined => {
    if (!s.period || !prepared.comparisonMetricLabel || !prepared.priorXKey || !prepared.xIsDate) {
      return undefined;
    }
    const date = formatComparisonDate(
      s.period === 'current' ? (label ?? null) : (row?.[prepared.priorXKey] ?? null)
    );
    return date ? `${PERIOD_TITLES[s.period]} · ${date}` : undefined;
  };

  return (
    <div className='chart-renderer-tooltip'>
      <div className='chart-renderer-tooltip-label'>{header}</div>
      {orderTooltipEntries(payload, prepared).map(entry => {
        const key = typeof entry.dataKey === 'string' ? entry.dataKey : '';
        const s = lookup[key];
        return (
          <div key={key} className='chart-renderer-tooltip-row'>
            <span className='chart-renderer-tooltip-name'>
              {(s && datedPeriodLabel(s)) ?? s?.label ?? key}
            </span>
            <span className='chart-renderer-tooltip-value'>
              {s ? s.formatValue(entry.value ?? null) : formatChartValue(entry.value ?? null)}
            </span>
          </div>
        );
      })}
    </div>
  );
};
